<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { GameInteractionContent } from '~/model/GameInteraction/GameInteraction'
  import IconBoxed from '~/components/IconBoxed/IconBoxed.vue'
  import ResponsiveImg from '~/components/ResponsiveImg/ResponsiveImg.vue'
  import RewardBox from '~/components/RewardBox/RewardBox.vue'
  import useTranslation from '~/composables/Translation/Translation'
  import { computed } from '#imports'
  import { useGameStateStore } from '~/store/GameState'
  import { IconBoxedFacet } from '~/components/IconBoxed/IconBoxedFacet'
  import { VisualThemeId } from '~/model/VisualTheme/VisualTheme'
  import { UiIconId, UiIconSizeId } from '~/model/UiIcon/UiIcon'
  import useIsMounted, { UseIsMountedEmits } from '~/composables/IsMounted/IsMounted'

  interface Props {
    interactionContent?: GameInteractionContent
  }

  interface Emits extends UseIsMountedEmits {
    (e: 'mounted'): void
  }

  const gameStateStore = useGameStateStore()

  const emit = defineEmits<Emits>()
  const props = defineProps<Props>()

  const { completedInteractionList } = storeToRefs(gameStateStore)
  useIsMounted(emit)
  const { t } = useTranslation()

  const isLoaded = computed<boolean>(() => !!props.interactionContent)
  const interactionCategory = computed<string>(() => {
    return t(`interaction-category.${props.interactionContent?.category}`)
  })
  const firstLetter = computed<string>(() => interactionCategory.value.charAt(0))
  const isCompleted = computed<boolean>(() => {
    if (!props.interactionContent) {
      return false
    }
    return completedInteractionList.value.has(props.interactionContent.id)
  })
</script>

<template>
  <div class="c-interaction-head">
    <div class="s-container s-container--primary">
      <div class="s-container__container">
        <div class="c-interaction-head__aside">
          <slot name="label" />
        </div>

        <div class="c-interaction-head__main s-grid s-grid--primary">
          <ClientOnly>
            <div class="c-interaction-head__primary-data">
              <div class="c-interaction-head__icon">
                <IconBoxed
                  :facets="[IconBoxedFacet.XXXLarge, isCompleted ? IconBoxedFacet.Disabled : null]"
                  :visual-theme="VisualThemeId.Negative"
                >
                  <template #letter>
                    <template v-if="isCompleted">
                      <UiIcon
                        :id="UiIconId.IconControlLargeCheckmark"
                        :colorize="true"
                        :size="UiIconSizeId.XLarge"
                      />
                    </template>
                    <template v-else>
                      {{ firstLetter }}
                    </template>
                  </template>
                </IconBoxed>
              </div>
              <div class="c-interaction-head__title">
                <h1 v-if="isLoaded">
                  {{ interactionCategory }}:
                  {{ interactionContent.name }}
                </h1>
                <dl v-if="isLoaded" class="u-reset c-interaction-head__meta-data-list">
                  <div
                    v-if="interactionContent.duration"
                    class="u-reset c-interaction-head__meta-data-entry"
                  >
                    <dt class="c-interaction-head__meta-data-entry-key">
                      {{ t('interaction-head.duration') }}:
                    </dt>
                    <dd class="c-interaction-head__meta-data-entry-value">
                      {{
                        t('interaction-head.duration-minutes', {
                          duration: interactionContent.duration,
                        })
                      }}
                    </dd>
                  </div>
                  <div
                    v-if="interactionContent.difficulty"
                    class="u-reset c-interaction-head__meta-data-entry"
                  >
                    <dt class="c-interaction-head__meta-data-entry-key">
                      {{ t('interaction-head.difficulty') }}:
                    </dt>
                    <dd class="c-interaction-head__meta-data-entry-value">
                      {{ t(`interaction-difficulty.${interactionContent.difficulty}`) }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div class="c-interaction-head__secondary-data s-grid s-grid--primary">
              <div class="c-interaction-head__image-area">
                <ResponsiveImg
                  v-if="isLoaded"
                  :src="interactionContent.illustration"
                  :width="300"
                  alt=""
                  class="c-interaction-head__image"
                />
              </div>
              <div class="c-interaction-head__reward-area">
                <RewardBox v-if="isLoaded" :interaction-id="interactionContent.id">
                  <template #description>
                    <template v-if="isCompleted">
                      {{ t('interaction-head.reward-box.description-finished') }}
                    </template>
                    <template v-else>
                      {{ t('interaction-head.reward-box.description') }}
                    </template>
                  </template>
                </RewardBox>
              </div>
            </div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/base/font/font' as font;
  @use '/assets/scss/base/typography/typography' as type;
  @use '/components/NavigationGame/NavigationGame' as NavigationGame;

  .c-interaction-head {
    padding: 0 0 80px;
  }

  .c-interaction-head__aside {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: NavigationGame.$height;
  }

  .c-interaction-head__main {
    padding-top: 40px;
    row-gap: 60px;
  }

  .c-interaction-head__primary-data {
    display: flex;
    flex-flow: row nowrap;
    grid-column: 2 / 12;
    column-gap: 32px;
    row-gap: 32px;
  }

  .c-interaction-head__icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .c-interaction-head__secondary-data {
    display: grid;
    grid-column: 2 / 12;
    column-gap: inherit;
    grid-template-areas: 'a a a b b b b b b b';
    grid-template-columns: repeat(10, 1fr);
  }

  .c-interaction-head__title {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    flex: 0 1 100%;
  }

  .c-interaction-head__meta-data-list {
    display: flex;
    flex-flow: row wrap;
    width: 100%;
    column-gap: 20px;
  }

  .c-interaction-head__meta-data-entry {
    display: flex;
    flex-flow: row nowrap;
    column-gap: 4px;
  }

  .c-interaction-head__meta-data-entry-key {
    @include font.base-bold;
  }

  .c-interaction-head__image-area {
    grid-area: a;
    overflow: hidden;
  }

  .c-interaction-head__reward-area {
    grid-area: b;
    overflow: hidden;
  }
</style>
